import React, {
  Fragment,
  ReactElement,
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import { Dialog, Tab, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';

import {
  Button,
  Spinner,
  CustomCancelModal,
  Alert,
  Badge,
} from 'src/ui/components';
import { PersonDetailsTab } from './components';
import { QuestionsTab } from './Questions';

import { useAuth, useGetPhoneType, useGetStoredPermissions } from 'src/hooks';
import { useEditPersonDetails } from '../../api';
import { useMutableObserver } from 'src/hooks/useMutableObserver';
import { useUpdatePreferredResponses } from '../../api/useUpdatePreferredResponses';

import { formatSelectOptionWithoutSelect } from 'src/utils/common';
import { classNames } from 'src/utils/className';
import {
  successMsg,
  warningMsg,
  errorMsg,
  cancelAlertWarning,
  restrictedQuestions,
  restrictedTypes,
} from '../../utils/personRepoConstants';
import { showErrorToast, showSuccessToast } from 'src/utils/ToastNotification';
import { usePersonRepoStore } from '../../store/personRepoStore';

import {
  PreferredResponses,
  Sections,
  Response,
} from '../../types/QuestionsTabType';

type ModalType = {
  showModal: boolean;
  setShowModal: (val: boolean) => void;
  refetchChangeHistoryList: () => void;
};

function ViewEditModal({
  showModal,
  setShowModal,
  refetchChangeHistoryList,
}: ModalType): ReactElement {
  const { token, tokenType } = useAuth();

  const queryClient = useQueryClient();
  const warningMsgRef = useRef<HTMLDivElement | null>(null);
  const dateRangewrapper = useRef<any>();

  const permissions = useGetStoredPermissions();

  const { isMessageChanged } = useMutableObserver(warningMsgRef);

  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);

  // to store current tab
  const [currentTab, setCurrentTab] = useState<string>('personTab');
  // search postal code in Address - moved to parent to clear the search postal code when reset
  const [postalCode, setPostalCode] = useState<string>('');

  const personDetails = usePersonRepoStore(
    useCallback(state => state.personDetails, []),
  );

  const userMode = usePersonRepoStore(useCallback(state => state.userMode, []));

  const setUserMode = usePersonRepoStore(
    useCallback(state => state.setUserMode, []),
  );

  const enableSaveBtn = usePersonRepoStore(
    useCallback(state => state.enableSaveBtn, []),
  );

  const questionDet = usePersonRepoStore(
    useCallback(state => state.question, []),
  );

  const isSaveClicked = usePersonRepoStore(
    useCallback(state => state.isSaveClicked, []),
  );

  const setIsSaveClicked = usePersonRepoStore(
    useCallback(state => state.setIsSaveClicked, []),
  );

  const isPersonInformationLoading = usePersonRepoStore(
    useCallback(state => state.isPersonInformationLoading, []),
  );

  const isQuestionInformationLoading = usePersonRepoStore(
    useCallback(state => state.isQuestionInformationLoading, []),
  );

  const resetStore = usePersonRepoStore(
    useCallback(state => state.resetStore, []),
  );

  const setSelectedPerson = usePersonRepoStore(
    useCallback(state => state.setSelectedPerson, []),
  );

  const showPrimaryNumberError = usePersonRepoStore(
    useCallback(state => state.showPrimaryNumberError, []),
  );

  const showSecondaryNumberError = usePersonRepoStore(
    useCallback(state => state.showSecondaryNumberError, []),
  );

  const checkDataHasChanged = usePersonRepoStore(
    useCallback(state => state.checkDataHasChanged, []),
  );

  const checkPersonRepoDataHasChanged = usePersonRepoStore(
    useCallback(state => state.checkPersonRepoDataHasChanged, []),
  );

  const formValues = usePersonRepoStore(
    useCallback(state => state.formValues.Poc, []),
  );

  const previousFormValues = usePersonRepoStore(
    useCallback(state => state.formValues.PocPreviousData, []),
  );

  const getPersonInformation = usePersonRepoStore(
    useCallback(state => state.updatePersonInformation, []),
  );

  const resetFormValues = usePersonRepoStore(
    useCallback(state => state.resetFormValues, []),
  );

  const { data: phoneTypes, isLoading: isPhoneTypeLoading } = useGetPhoneType();
  const { mutate: editPersonDetails, isLoading: isUpdating } =
    useEditPersonDetails(true);

  const {
    mutate: editPreferredResponses,
    isLoading: isEditPreferredResponesLoading,
  } = useUpdatePreferredResponses();

  /*disable the save button if validation is thrown in FE */
  const showEmailValidation = usePersonRepoStore(
    React.useCallback(state => state.showEmailValidation, []),
  );

  /*disable the save button if validation is thrown in FE */
  const showUrlValidation = usePersonRepoStore(
    React.useCallback(state => state.showUrlValidation, []),
  );

  /*disable the save button if validation is thrown in FE */
  const setShowEmailValidation = usePersonRepoStore(
    React.useCallback(state => state.setShowEmailValidation, []),
  );

  /*disable the save button if validation is thrown in FE */
  const setShowUrlValidation = usePersonRepoStore(
    React.useCallback(state => state.setShowUrlValidation, []),
  );

  const isSpinnerLoading =
    isPersonInformationLoading ||
    isPhoneTypeLoading ||
    isUpdating ||
    isEditPreferredResponesLoading ||
    isQuestionInformationLoading;

  const phoneTypeList = formatSelectOptionWithoutSelect(
    phoneTypes,
    'Id',
    'Description',
  );

  const updatePhone = (phoneDetails: any) => ({
    ...phoneDetails,
    PhoneTypeId:
      phoneDetails.PhoneTypeId === '-1' ? null : phoneDetails.PhoneTypeId,
    PhoneType:
      phoneDetails.PhoneTypeId === '-1' ? null : phoneDetails.PhoneType,
  });

  const personName = useMemo(() => {
    return `${personDetails.FirstName} ${personDetails.LastName}`;
  }, [personDetails]);

  //for save person details
  function onSavePersonDetails() {
    const firstName = personDetails?.FirstName?.trim();
    const lastName = personDetails?.LastName?.trim();
    const prefix = personDetails?.Prefix?.trim();
    const suffix = personDetails?.Suffix?.trim();
    const preferredName = personDetails?.PreferredName?.trim();
    const genderId =
      personDetails?.GenderId === '-1' || personDetails?.GenderId === -1
        ? null
        : personDetails?.GenderId;

    if (
      firstName === undefined ||
      lastName === undefined ||
      firstName === '' ||
      lastName === ''
    ) {
      return;
    }

    const updatedPersonDetails = {
      ...personDetails,
      FirstName: firstName,
      LastName: lastName,
      Prefix: prefix,
      Suffix: suffix,
      PreferredName: preferredName,
      Age: personDetails?.Age ? personDetails?.Age : '',
      GenderId: genderId,
      PrimaryPhone: updatePhone(personDetails.PrimaryPhone),
      SecondaryPhone: updatePhone(personDetails.SecondaryPhone),
      Birthdate: personDetails.Birthdate
        ? dayjs(personDetails.Birthdate).format('MM/DD/YYYY')
        : null,
    };

    editPersonDetails(updatedPersonDetails, {
      onSuccess: () => {
        showSuccessToast({ message: successMsg });
        setShowModal(false);
        setIsSaveClicked(false);
        resetStore();
        setPostalCode('');
        queryClient.invalidateQueries(['get_person_repo_list'], {
          refetchActive: true,
        });
        refetchChangeHistoryList();
        setSelectedPerson(undefined);
      },
    });
  }

  function getFieldLabelByQuestionTemplateId(targetId: number) {
    if (questionDet?.Survey?.Sections) {
      for (const section of questionDet?.Survey?.Sections) {
        for (const question of section.Questions || []) {
          if (question.QuestionTemplateId === targetId) {
            return question.QuestionTemplate?.FieldLabel || null;
          }
        }
      }
    }
    return null;
  }

  function onQuestionUpdateHandler() {
    if (showEmailValidation || showUrlValidation) return;
    const personId = usePersonRepoStore.getState().personDetails?.Id;
    const accountId = usePersonRepoStore.getState().personDetails?.AccountId;
    const entityId = usePersonRepoStore.getState().personDetails?.EntityId;
    const updatedDate = new Date().toISOString();
    const result: PreferredResponses[] = [];

    Object.entries(formValues).forEach(([category, values]) => {
      if (typeof values === 'object' && values !== null) {
        Object.entries(values).forEach(([questionTemplateId, value]) => {
          const questionId = Number(questionTemplateId);
          const fieldLabel = getFieldLabelByQuestionTemplateId(questionId);

          const transformedValue =
            value === null
              ? null
              : Array.isArray(value)
              ? value.join('|')
              : typeof value === 'object'
              ? JSON.stringify(value)
              : String(value);

          const prevValue =
            previousFormValues?.[category]?.[questionTemplateId];

          const prevTransformedValue =
            prevValue === null
              ? null
              : Array.isArray(prevValue)
              ? prevValue.join('|')
              : typeof prevValue === 'object'
              ? JSON.stringify(prevValue)
              : String(prevValue);

          if (
            prevValue === undefined ||
            transformedValue !== prevTransformedValue
          ) {
            result.push({
              PersonId: entityId,
              QuestionTemplateId: questionId,
              PreviousValue: prevTransformedValue,
              Value: transformedValue,
              QuestionFieldLabel: fieldLabel,
              UpdatedDate: updatedDate,
              ValueModified: 'true',
            });
          }
        });
      }
    });

    editPreferredResponses(result, {
      onSuccess: () => {
        queryClient.invalidateQueries(['get-person-question-details'], {
          refetchActive: true,
        });
        getPersonInformation(accountId, personId, entityId, token, tokenType);
        setUserMode('Edit');
        refetchChangeHistoryList();
        setSelectedPerson(undefined);
        showSuccessToast({ message: successMsg });
      },
      onError: () => {
        showErrorToast({ message: errorMsg });
      },
    });
  }

  //for getting button label
  function getLabel() {
    if (userMode === 'Edit') {
      if (currentTab === 'personTab') return 'Edit personal details';
      if (currentTab === 'questionTab') return 'Edit questions';
    } else {
      if (currentTab === 'personTab') return 'Save personal details';
      if (currentTab === 'questionTab') return 'Save questions';
    }
  }

  //for handle cancel
  function handleCancel() {
    resetStore();
    setPostalCode('');
    setCurrentTab('personTab');
    setUserMode('Edit');
    setIsSaveClicked(false);
    queryClient.invalidateQueries(['get-person-details'], {
      refetchActive: false,
    });
    queryClient.invalidateQueries(['get-person-question-details'], {
      refetchActive: false,
    });
  }

  function handleQuestionCancel() {
    resetFormValues();
    setCurrentTab('questionTab');
    setIsSaveClicked(false);
    setUserMode('Edit');
    setShowEmailValidation(false);
    setShowUrlValidation(false);
    queryClient.invalidateQueries(['get-person-details'], {
      refetchActive: false,
    });
    queryClient.invalidateQueries(['get-person-question-details'], {
      refetchActive: false,
    });
  }

  function shouldDisableButton(): boolean {
    const noPermission =
      !permissions?.CallCenterSupervisors && !permissions?.RelationshipManager;

    const isPersonTabDirty =
      currentTab === 'personTab' &&
      userMode === 'Update' &&
      checkDataHasChanged();

    const isQuestionTabDirty =
      currentTab === 'questionTab' &&
      userMode === 'Update' &&
      checkPersonRepoDataHasChanged();

    const hasNumberErrors = showPrimaryNumberError || showSecondaryNumberError;

    return (
      noPermission ||
      isPersonTabDirty ||
      isQuestionTabDirty ||
      hasNumberErrors ||
      isEditPreferredResponesLoading ||
      (currentTab === 'questionTab' && reportData?.length === 0) ||
      (currentTab === 'questionTab' &&
        userMode === 'Update' &&
        showEmailValidation) ||
      (currentTab === 'questionTab' &&
        userMode === 'Update' &&
        showUrlValidation)
    );
  }

  // Sections & Questions
  const reportData = useMemo(() => {
    if (questionDet) {
      const { CallerResponses, PersonOfConcernResponses, Survey } = questionDet;

      const temp: any =
        Survey?.Sections &&
        Survey?.Sections.length > 0 &&
        Survey?.Sections.map(item => {
          if (item.hasOwnProperty('Section')) {
            return {
              ...item.Section,
              Id: item?.Id,
              $type:
                'Proteus2.Api.Domain.Surveys.GlobalSectionReference, Proteus2.Api.Domain',
              Order: item?.Order,
            };
          } else {
            return { ...item };
          }
        });
      const optimizedResult = (temp || [])
        ?.map((section: Sections) => {
          const { Title, Order, Id, Questions } = section;
          const filteredQuestions = Questions?.filter(q => {
            const type = q.QuestionTemplate?.QuestionType?.Description;
            return type && !restrictedTypes.has(type);
          }).filter(
            item =>
              item.PersonAssociation === 1 || item.PersonAssociation === 0,
          );

          const result = {
            Id,
            Title,
            Order,
            Questions: filteredQuestions?.map(question => {
              let Value = '';

              let responseList: Array<Response> = [];
              if (question.PersonAssociation === 0) {
                responseList = CallerResponses;
              } else if (question.PersonAssociation === 1) {
                responseList = PersonOfConcernResponses;
              }

              const foundResponse = responseList?.find(
                (response: Response) =>
                  response.QuestionTemplateId === question.QuestionTemplateId,
              );
              Value = foundResponse?.Value ?? '';
              return {
                Id: question.Id,
                FieldLabel: question.FieldLabel,
                Order: question.Order,
                Value,
                TemplateId: question.QuestionTemplateId,
                Type:
                  question.QuestionTemplate?.QuestionType?.Description ?? '',
                TypeValue:
                  question.QuestionTemplate?.QuestionType?.Description ===
                  'Choice'
                    ? question?.QuestionTypeOptions?.Type
                    : null,
                TextTypeOptions:
                  question.QuestionTemplate?.QuestionType?.Description ===
                  'Text'
                    ? question?.QuestionTypeOptions
                    : null,
                Options:
                  question.QuestionTemplate?.QuestionType?.Description ===
                    'Choice' && question?.QuestionOptions.length > 0
                    ? question.QuestionOptions.map(
                        ({ AccessTypeId, QuestionTemplateOption }) => ({
                          Id: QuestionTemplateOption?.Id,
                          ItemLabel: QuestionTemplateOption.ItemLabel,
                          ItemName: QuestionTemplateOption.ItemName,
                          AccessTypeId: AccessTypeId,
                        }),
                      )
                    : question.QuestionTemplate?.QuestionType?.Description ===
                        'Lookup' &&
                      question.QuestionTypeOptions?.MappedLookupColumns &&
                      question.QuestionTypeOptions?.MappedLookupColumns
                        ?.length > 0
                    ? question.QuestionTypeOptions?.MappedLookupColumns
                    : [],
              };
            }),
          };
          return result.Questions?.length > 0 ? result : null;
        })
        .filter(Boolean);
      return optimizedResult;
    }
    return [];
  }, [questionDet]);

  function onCloseClick() {
    const isPersonTab = currentTab === 'personTab';
    const isQuestionTab = currentTab === 'questionTab';

    if (isPersonTab) {
      if (userMode === 'Update') {
        if (!checkDataHasChanged()) {
          setShowCancelModal(true);
        } else {
          setUserMode('Edit');
          handleCancel();
          resetStore();
        }
      } else if (userMode === 'Edit') {
        setShowModal(false);
        queryClient.invalidateQueries(['get-person-details'], {
          refetchActive: false,
        });
        queryClient.invalidateQueries(['get-person-question-details'], {
          refetchActive: false,
        });
      }
    } else if (isQuestionTab) {
      if (userMode === 'Edit') {
        // handleQuestionCancel();
        setShowModal(false);
        queryClient.invalidateQueries(['get-person-question-details'], {
          refetchActive: false,
        });
        queryClient.invalidateQueries(['get-person-details'], {
          refetchActive: false,
        });
      } else if (userMode === 'Update' && !checkPersonRepoDataHasChanged()) {
        setShowCancelModal(true);
      } else {
        setUserMode('Edit');
        handleQuestionCancel();
      }
    }
  }

  function renderFooterButtons() {
    return (
      <div
        ref={warningMsgRef}
        className="flex flex-col justify-end gap-[1ch] border-t bg-gray-50 p-2 dark:border-dark-medium dark:bg-neutral-700/50 sm:flex-row  sm:p-4"
      >
        {isSaveClicked && !enableSaveBtn() && currentTab === 'personTab' && (
          <div
            className={`flex items-center gap-2 ${
              isMessageChanged ? 'animate-shake' : ''
            }`}
          >
            <span className="relative flex h-3 w-3 shrink-0 items-center justify-center">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
            </span>
            <span className="text-sm font-medium text-red-700 dark:text-red-400">
              {warningMsg}
            </span>
          </div>
        )}
        <div className="flex flex-col  justify-between gap-[1ch] md:flex-row md:items-center">
          {checkDataHasChanged() === false && currentTab === 'questionTab' && (
            <Alert variant="warning" size="small">
              {cancelAlertWarning}
            </Alert>
          )}
          <div className="flex gap-[1ch]">
            <Button
              variant="secondary"
              className="grow sm:grow-0"
              onClick={() => {
                onCloseClick();
              }}
            >
              {userMode === 'Edit' ? 'Close' : 'Cancel'}
            </Button>
            <Button
              variant="primary"
              className="grow sm:grow-0"
              onClick={() => {
                setUserMode('Update');
                if (currentTab === 'personTab' && userMode === 'Update') {
                  setIsSaveClicked(true);
                  onSavePersonDetails();
                }
                if (currentTab === 'questionTab' && userMode === 'Update') {
                  setIsSaveClicked(true);
                  onQuestionUpdateHandler();
                }
              }}
              disabled={shouldDisableButton()}
            >
              {getLabel()}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Transition appear show={showModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative"
          onClose={() => {
            onCloseClick();
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="pr-dialog__backdrop" />
          </Transition.Child>

          <div className="pr-dialog__wrapper">
            <div className="pr-dialog__panel__container">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="pr-dialog__panel flex h-[calc(100svh-110px)] max-h-[calc(100svh-110px)] flex-col sm:!max-w-5xl">
                  {isSpinnerLoading && (
                    <div className="bg-gray-900/8  absolute inset-0 z-50 grid place-items-center backdrop-blur-sm">
                      <Spinner size="large" />
                    </div>
                  )}

                  <div className="flex items-center gap-3 border-b p-4 dark:border-dark-medium">
                    <Dialog.Title
                      as="h3"
                      className="grow truncate text-lg font-medium leading-6 text-gray-900 dark:text-white"
                    >
                      {currentTab === 'personTab'
                        ? personName
                        : questionDet?.Survey?.Account?.AccountNameAndNumber}
                    </Dialog.Title>

                    <button
                      type="button"
                      className=" text-gray-400 hover:text-gray-500 sm:right-4 sm:top-4"
                      onClick={() => onCloseClick()}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  <Tab.Group>
                    <Tab.List className="flex justify-center gap-5 border-b border-gray-200 dark:border-neutral-700">
                      <Tab
                        as="button"
                        disabled={
                          currentTab === 'questionTab' &&
                          !checkPersonRepoDataHasChanged()
                        }
                        title={
                          currentTab === 'questionTab' &&
                          !checkPersonRepoDataHasChanged()
                            ? `You have unsaved changes in Questions tab.`
                            : ''
                        }
                        className={classNames(
                          'flex gap-2 border-b-2 border-b-transparent px-2 py-2.5 font-medium leading-5',
                          ' focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-light-focus active:translate-y-px active:shadow-none dark:focus-visible:outline-dark-focus',
                          'ui-selected:border-b-primary-600 ui-selected:text-primary-600 dark:ui-selected:border-b-primary-600 dark:ui-selected:text-primary-300',
                          'disabled:opacity-50',
                        )}
                        onClick={() => {
                          setCurrentTab('personTab');
                          if (currentTab !== 'personTab') setUserMode('Edit');
                        }}
                      >
                        <span>Personal details</span>
                        {currentTab === 'personTab' &&
                          !isPersonInformationLoading &&
                          !checkDataHasChanged() && (
                            <Badge variant="info">Editing</Badge>
                          )}
                      </Tab>

                      <Tab
                        as="button"
                        disabled={
                          currentTab === 'personTab' && !checkDataHasChanged()
                        }
                        title={
                          currentTab === 'personTab' && !checkDataHasChanged()
                            ? `You have unsaved changes in Personal details tab.`
                            : ''
                        }
                        className={classNames(
                          'flex gap-2 border-b-2 border-b-transparent px-2 py-2.5 font-medium leading-5',
                          ' focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-light-focus active:translate-y-px active:shadow-none dark:focus-visible:outline-dark-focus',
                          'ui-selected:border-b-primary-600 ui-selected:text-primary-600 dark:ui-selected:border-b-primary-600 dark:ui-selected:text-primary-300',
                          'disabled:opacity-50',
                        )}
                        onClick={() => {
                          setCurrentTab('questionTab');
                          if (currentTab !== 'questionTab') setUserMode('Edit');
                          // onQuestionTabClickHandler();
                        }}
                      >
                        <span>Questions</span>
                        {currentTab === 'questionTab' &&
                          !checkPersonRepoDataHasChanged() && (
                            <Badge variant="info">Editing</Badge>
                          )}
                      </Tab>
                    </Tab.List>

                    <Tab.Panel
                      className="relative min-h-0 grow overflow-y-scroll scroll-smooth p-4 lg:p-6"
                      ref={dateRangewrapper}
                    >
                      <PersonDetailsTab
                        refValue={dateRangewrapper}
                        postalCode={postalCode}
                        setPostalCode={setPostalCode}
                      />
                    </Tab.Panel>

                    <Tab.Panel className="relative min-h-0 grow overflow-y-scroll scroll-smooth px-4 lg:px-6">
                      <QuestionsTab reportData={reportData} />
                    </Tab.Panel>
                  </Tab.Group>

                  {renderFooterButtons()}
                </Dialog.Panel>
              </Transition.Child>
            </div>
            {showCancelModal &&
              ((currentTab === 'personTab' && !checkDataHasChanged()) ||
                (currentTab === 'questionTab' &&
                  !checkPersonRepoDataHasChanged())) && (
                <CustomCancelModal
                  setShowCancelModal={setShowCancelModal}
                  showCancelModal={showCancelModal}
                  handleCancel={
                    currentTab === 'personTab'
                      ? handleCancel
                      : handleQuestionCancel
                  }
                  component="PersonRepo"
                />
              )}
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

export { ViewEditModal };
